// heatScale.js — port of TamCentralStation src/ui/components/heatScale.js.
// Same ramps, binning and ink selection; the Meridian base-color tokens are
// inlined as their hex values (from @amzn/meridian-tokens base/color).
const C = {
  colorGray0: '#FFFFFF', colorGray800: '#0B0C0C',
  colorGold200: '#FFE8B1', colorGold400: '#FCC201',
  colorTangerine300: '#FFAE5D', colorTangerine400: '#FF8F00',
  colorCrimson100: '#FFD9CF', colorCrimson200: '#FFB09F', colorCrimson300: '#F98771',
  colorCrimson400: '#EE5A46', colorCrimson500: '#DE1B1B', colorCrimson600: '#AF1F18',
  colorForest100: '#D6ECC9', colorForest200: '#ABD994', colorForest300: '#7EC55E',
  colorForest400: '#47B120', colorForest500: '#348118', colorForest600: '#005C00',
  colorBlue50: '#E5F6FB', colorBlue100: '#B9E8F7', colorBlue200: '#7CCEE9',
  colorBlue300: '#47C2EC', colorBlue400: '#00A8E1', colorBlue500: '#077398',
};

export const HEAT_SCALES = Object.freeze({
  heat:  [C.colorGold200, C.colorGold400, C.colorTangerine300, C.colorTangerine400, C.colorCrimson400, C.colorCrimson600],
  green: [C.colorForest100, C.colorForest200, C.colorForest300, C.colorForest400, C.colorForest500, C.colorForest600],
  blue:  [C.colorBlue50, C.colorBlue100, C.colorBlue200, C.colorBlue300, C.colorBlue400, C.colorBlue500],
  red:   [C.colorCrimson100, C.colorCrimson200, C.colorCrimson300, C.colorCrimson400, C.colorCrimson500, C.colorCrimson600],
});

function relativeLuminance(hex) {
  const channel = (offset) => {
    const v = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}
function contrastRatio(a, b) {
  const la = relativeLuminance(a), lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
function inkFor(fill) {
  return contrastRatio(fill, C.colorGray800) >= contrastRatio(fill, C.colorGray0) ? C.colorGray800 : C.colorGray0;
}
const HEAT_CELLS = Object.fromEntries(Object.entries(HEAT_SCALES).map(([name, ramp]) =>
  [name, ramp.map(background => ({ background, color: inkFor(background) }))]));

function cellsFor(name) { return HEAT_CELLS[name] || HEAT_CELLS.heat; }

export function heatRatioCell(ratio, scaleName = 'heat') {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio)) return null;
  const cells = cellsFor(scaleName);
  const clamped = Math.min(1, Math.max(0, ratio));
  return cells[Math.min(cells.length - 1, Math.floor(clamped * cells.length))];
}

export function heatCell(value, max, scaleName = 'heat') {
  if (typeof value !== 'number' || typeof max !== 'number') return null;
  if (!Number.isFinite(value) || !Number.isFinite(max)) return null;
  if (value <= 0 || max <= 0) return null;
  return heatRatioCell(value / max, scaleName);
}

// Meridian light-theme card chrome used by TCS's grids.
export const CHROME = {
  border: '1px solid #E7E9E9',       // card border (gray200)
  outline: '#077398',
  gray: { background: '#F0F1F2', color: '#697576' },  // surface disabled / ink subdued
};
